import React, { useState, useEffect } from 'react';
import { useReportDetailsNew } from '../../hooks/useReportDetailsNew';
import OrganizationDetails from './OrganizationDetails';
import ReportDetails from './ReportDetails';
import ReportSummary from './ReportSummary';
import SimilarOrganizationsList from './SimilarOrganizations/SimilarOrganizationsList';
import DomainDataSection from './DomainDataSection';
import ReportEmployees from './Employees/ReportEmployees';
import EmployeeDetails from './Employees/EmployeeDetails';
import UrlDataSection from './UrlDataSection';
import { ReportData, EmployeeDetail } from '../../types/interfaces';
import Loader from '../Loader';
import { useTranslation } from 'react-i18next';
import AssistantChat from './Chat/AssistantChat';
import { Search, Users } from 'lucide-react';

type Props = {
  reportId: string;
  summaryData?: ReportData;
};

export const ReportDetailComponent: React.FC<Props> = ({ reportId, summaryData }) => {
  const { t } = useTranslation();
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeDetail | null>(null);
  const { data, loading, error, fetchReportDetails } = useReportDetailsNew(reportId);

  const isTestReport = summaryData?.company_logo_url === 'damelio';

  useEffect(() => {
    fetchReportDetails();
  }, [reportId, fetchReportDetails]);

  if (loading) {
    return <Loader />;
  }
  if (error) return <p className="text-red-500">{error}</p>;
  if (!data) return <p>{t('reportDetails.no_details_available')}</p>;

  const domainObjects = data.domains.map((domain, index) => ({
    id: index,
    domain_name: domain,
  }));

  const urlObjects = data.urls.map((url, index) => ({
    id: index,
    url,
  }));

  const domainDetails = data.domain_details;
  const urlDetails = data.url_details;

  return (
    <div className="relative">
      {/* ✨ 2) Si es test, mostramos banner amarillo arriba */}
      {isTestReport && (
        <div className="bg-yellow-100 border-l-4 border-yellow-400 text-yellow-800 p-4 mb-4 rounded-md shadow">
          <p className="font-bold">{t('reportDetails.testReportTitle')}</p>
          <p>{t('reportDetails.testReportExplanation')}</p>
        </div>
      )}

      <div>
        {/* Sección de Organizaciones y Detalles del Reporte */}
        <div className="flex flex-col lg:flex-row lg:space-x-8 mt-6">
          <div className="lg:w-1/2 mb-8 lg:mb-0">
            {data.organizations.length > 0 ? (
              data.organizations.map((org, idx) => (
                <OrganizationDetails key={idx} organization={org} />
              ))
            ) : (
              <p className="text-gray-600">
                {t('reportDetails.no_organization_info')}
              </p>
            )}
          </div>
          <div className="lg:w-1/2">
            <ReportDetails
              domains={domainObjects}
              urls={urlObjects}
              created_at={new Date(data.created_at)}
              reportDownloadUrls={data.report_download_urls}
              summaryDownloadUrls={data.summary_download_urls}
            />
          </div>
        </div>

        {/* Resumen */}
        {summaryData && (
          <div className="mt-6">
            <ReportSummary summaryData={summaryData} />
          </div>
        )}

        {/* Empleados */}
        {data.employees && data.employees.length > 0 ? (
          <div className="mt-6">
            <ReportEmployees
              employees={data.employees}
              onViewDetails={(emp) => setSelectedEmployee(emp)}
            />
          </div>
        ) : (
          <div className="mt-6">
            <div className="flex flex-col items-center justify-center bg-gray-50 text-gray-700 border border-gray-300 rounded-lg p-6 text-center">
              <Users className="w-10 h-10 mb-2 text-gray-500" />
              <p className="text-lg font-medium">{t('reportDetails.no_employees_detected')}</p>
              <p className="text-sm text-gray-600 mt-2">
                {t('reportDetails.no_employees_detected_description')}
              </p>
            </div>
          </div>
        )}

        {/* Empresas similares */}
        {data.similar_organizations && data.similar_organizations.length > 0 ? (
          <div className="mt-6">
            <SimilarOrganizationsList similarOrganizations={data.similar_organizations} />
          </div>
        ) : (
          <div className="mt-6">
            <div className="flex flex-col items-center justify-center bg-gray-50 text-gray-700 border border-gray-300 rounded-lg p-6 text-center">
              <Search className="w-10 h-10 mb-2" />
              <p className="text-lg font-medium">{t('reportDetails.no_similar_companies')}</p>
              <p className="text-sm text-gray-600 mt-2">
                {t('reportDetails.no_similar_companies_description', {
                  company_name: summaryData?.company_name || 'esta empresa'
                })}
              </p>
            </div>
          </div>
        )}

        {/* Sección de Dominios */}
        <DomainDataSection domainObjects={domainObjects} domainDetails={domainDetails} />

        {/* Sección de URLs (Directorios, WAF, Tecnologías...) */}
        <UrlDataSection
          selectedUrl={urlObjects.length > 0 ? urlObjects[0].url : null}
          urls={urlObjects}
          onUrlChange={(url) => console.log(t('reportDetails.url_changed', { url }))}
          urlDetails={urlDetails}
        />
      </div>
      
      {/* Chat de asistencia */}
      {!isTestReport && (
        <AssistantChat reportId={reportId} />
      )}
      {/* Panel lateral para detalles de empleado */}
      {selectedEmployee && (
        <>
          <div
            className="fixed inset-0 bg-black opacity-50 z-40"
            onClick={() => setSelectedEmployee(null)}
          ></div>
          <div className="fixed top-0 right-0 h-full w-[750px] bg-white shadow-xl z-50 overflow-auto">
            <EmployeeDetails
              employee={selectedEmployee}
              onBack={() => setSelectedEmployee(null)}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ReportDetailComponent;
